import React from "react";
import { Box, Chip, Drawer, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Shield as ShieldIcon,
} from "@mui/icons-material";
import { useClinicTheme } from "../../theme";
import { COLORS, TYPO } from "../../styles/uiConstants";
import { DrawerField, tileInputProps } from "../../components/molecules";
import { DrawerHeader, DrawerFooter, FormTextField } from "../../components/atoms";
import { ADMIN_PERMISSIONS, getPermLabel } from "./constants";

/**
 * Şablon oluşturma/düzenleme drawer'ı. Alan yapısı "Personel Oluştur"
 * drawer'ıyla aynı: InfoTile + standard variant input.
 */
export default function RoleTemplateFormDrawer({
  open,
  mode = "create",
  isDefault = false,
  name,
  onNameChange,
  permissions = [],
  onTogglePermission,
  onToggleGroup,
  onClose,
  onSubmit,
}) {
  const { palette } = useClinicTheme();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 640 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<ShieldIcon />}
        title={mode === "edit" ? "Şablon Düzenle" : "Yeni Şablon"}
        subtitle="AdminStaff izinlerini belirleyin"
        onClose={onClose}
      />

      {/* Body */}
      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: COLORS.white, flex: 1, minHeight: 0, overflowY: "auto" }}>
        <DrawerField label="Şablon Adı *" fullWidth>
          {mode === "edit" && isDefault ? (
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: COLORS.slate800 }}>
              {name}
            </Typography>
          ) : (
            <FormTextField
              value={name}
              onChange={(e) => onNameChange?.(e.target.value)}
              placeholder="örn: Muhasebe, Depo Yetkilisi..."
              fullWidth
              size="small"
              variant="standard"
              required
              inputProps={{ "aria-label": "Şablon Adı" }}
              InputProps={tileInputProps(true)}
            />
          )}
        </DrawerField>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2.5, mb: 1.25 }}>
          <Typography
            variant="caption"
            sx={{ color: COLORS.slate500, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}
          >
            İzinler
          </Typography>
          <Chip
            label={`${permissions.length} seçili`}
            size="small"
            sx={{
              height: 22,
              fontSize: TYPO.caption,
              fontWeight: 700,
              bgcolor: alpha(palette.primary, 0.08),
              color: palette.primary,
            }}
          />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
          {ADMIN_PERMISSIONS.map(({ group, items }) => {
            const allSel = items.every((p) => permissions.includes(p));
            const selCount = items.filter((p) => permissions.includes(p)).length;
            return (
              <Box
                key={group}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${allSel ? alpha(palette.primary, 0.3) : COLORS.slate200}`,
                  overflow: "hidden",
                  bgcolor: COLORS.white,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={() => onToggleGroup?.(items)}
                  aria-label={`${group} izinlerinin tümünü seç`}
                  sx={{
                    width: "100%",
                    px: 2,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    border: "none",
                    font: "inherit",
                    textAlign: "left",
                    bgcolor: allSel ? alpha(palette.primary, 0.04) : "transparent",
                    borderBottom: `1px solid ${COLORS.slate100}`,
                    "&:hover": { bgcolor: COLORS.slate50 },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 1,
                        border: `2px solid ${allSel ? palette.primary : COLORS.slate300}`,
                        bgcolor: allSel ? palette.primary : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {allSel ? (
                        <Typography sx={{ color: COLORS.white, fontSize: 11, fontWeight: 900 }}>
                          &#x2713;
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: COLORS.slate800 }}>
                      {group}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: TYPO.caption, fontWeight: 600, color: COLORS.slate400 }}>
                    {selCount}/{items.length}
                  </Typography>
                </Box>

                <Box sx={{ px: 1 }}>
                  {items.map((perm, pi) => {
                    const checked = permissions.includes(perm);
                    return (
                      <Box
                        key={perm}
                        component="button"
                        type="button"
                        onClick={() => onTogglePermission?.(perm)}
                        aria-pressed={checked}
                        sx={{
                          width: "100%",
                          px: 1.5,
                          py: 0.75,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          border: "none",
                          bgcolor: "transparent",
                          font: "inherit",
                          textAlign: "left",
                          borderBottom: pi < items.length - 1 ? `1px solid ${COLORS.slate50}` : "none",
                          "&:hover": { bgcolor: COLORS.slate50 },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: checked ? COLORS.slate800 : COLORS.slate500,
                          }}
                        >
                          {getPermLabel(perm)}
                        </Typography>
                        <Box
                          sx={{
                            width: 34,
                            height: 18,
                            borderRadius: 9,
                            position: "relative",
                            bgcolor: checked ? palette.primary : COLORS.slate200,
                            transition: "background 0.2s",
                            flexShrink: 0,
                            ml: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              bgcolor: COLORS.white,
                              position: "absolute",
                              top: 2,
                              left: checked ? 18 : 2,
                              transition: "left 0.2s",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <DrawerFooter
        onCancel={onClose}
        onSubmit={onSubmit}
        submitLabel={mode === "edit" ? "Güncelle" : "Oluştur"}
        disabled={!name?.trim()}
        startIcon={<ShieldIcon sx={{ fontSize: 15 }} />}
        fullWidth
      />
    </Drawer>
  );
}
